"use client";

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Check, ChevronLeft, ChevronRight, ChevronDown, Play, Palette, Calendar, BarChart3, Mic2, BookOpen, PieChart, Rocket, Search, Users, TrendingUp, Zap, Building2, Flower2, Package, CheckCircle, Hexagon, Cloud, CircleDot, ShoppingCart, MessageSquare, FileText, Mail } from 'lucide-react';

// ============================================
// SHOWBOLD LANDING PAGE - Aurora Design
// Converted from Vue + Tailwind
// ============================================

export default function LandingPage() {
  const [scrolled, setScrolled] = useState(false);
  const [isAnnual, setIsAnnual] = useState(false);
  const [integrationIndex, setIntegrationIndex] = useState(0);

  // Optimized scroll handler with passive listener and RAF
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setScrolled(window.scrollY > 50);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Create particles on mount with RAF batching
  useEffect(() => {
    const container = document.getElementById('particles');
    if (!container) return;
    
    // Use RAF for smooth particle creation
    requestAnimationFrame(() => {
      const fragment = document.createDocumentFragment();
      for (let i = 0; i < 12; i++) {
        const particle = document.createElement('div');
        particle.className = 'particle';
        particle.style.cssText = `
          left: ${Math.random() * 100}%;
          animation-duration: ${Math.random() * 20 + 15}s;
          animation-delay: ${Math.random() * 10}s;
          width: ${Math.random() * 3 + 2}px;
          height: ${Math.random() * 3 + 2}px;
          background: hsl(${Math.random() * 60 + 250}, 70%, 70%);
        `;
        fragment.appendChild(particle);
      }
      container.appendChild(fragment);
    });

    return () => {
      if (container) container.innerHTML = '';
    };
  }, []);

  // Subtle reveal animations with reduced-motion support
  useEffect(() => {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const elements = Array.from(document.querySelectorAll<HTMLElement>('.reveal-on-scroll'));

    if (reduceMotion) {
      elements.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <>
      {/* Global Styles */}
      <style jsx global>{`
        body {
          font-family: 'Inter', sans-serif;
          overflow-x: hidden;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          text-rendering: optimizeLegibility;
        }

        /* Global performance optimizations */
        * {
          /* Smooth scrolling */
          scroll-behavior: smooth;
        }

        /* Optimize animations globally */
        @media (prefers-reduced-motion: no-preference) {
          html {
            scroll-behavior: smooth;
          }
        }

        /* Hardware acceleration hints for animated elements */
        .aurora-wrapper,
        .particles-container,
        section {
          isolation: isolate;
        }

        /* Global Aurora Background - Optimized for performance */
        .aurora-wrapper {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: -1;
          overflow: hidden;
          background: linear-gradient(135deg, #0a0a0a 0%, #1a0a2e 50%, #0a0a0a 100%);
          will-change: auto;
          contain: strict;
        }

        .aurora-layer {
          position: absolute;
          width: 200%;
          height: 200%;
          top: -50%;
          left: -50%;
          will-change: transform, opacity;
          backface-visibility: hidden;
          transform: translateZ(0);
        }

        .aurora-gradient-1 {
          background: radial-gradient(ellipse at 30% 20%, rgba(255, 0, 204, 0.3) 0%, transparent 50%);
          animation: aurora-pulse-1 12s ease-in-out infinite;
        }

        .aurora-gradient-2 {
          background: radial-gradient(ellipse at 70% 60%, rgba(0, 204, 255, 0.25) 0%, transparent 50%);
          animation: aurora-pulse-2 15s ease-in-out infinite;
        }

        .aurora-gradient-3 {
          background: radial-gradient(ellipse at 50% 80%, rgba(255, 153, 0, 0.25) 0%, transparent 50%);
          animation: aurora-pulse-1 18s ease-in-out infinite;
          animation-delay: -6s;
        }

        /* Simplified - only 3 active layers instead of 5 */
        .aurora-gradient-4,
        .aurora-gradient-5 {
          display: none;
        }

        @keyframes aurora-pulse-1 {
          0%, 100% { opacity: 0.5; transform: translateZ(0) scale(1); }
          50% { opacity: 0.8; transform: translateZ(0) scale(1.1); }
        }

        @keyframes aurora-pulse-2 {
          0%, 100% { opacity: 0.4; transform: translateZ(0) scale(1.05); }
          50% { opacity: 0.7; transform: translateZ(0) scale(0.95); }
        }

        /* Floating Particles - GPU optimized */
        .particles-container {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: -1;
          pointer-events: none;
          contain: strict;
        }

        .particle {
          position: absolute;
          width: 4px;
          height: 4px;
          background: white;
          border-radius: 50%;
          opacity: 0;
          will-change: transform, opacity;
          animation: particle-float 20s linear infinite;
        }

        @keyframes particle-float {
          0% { opacity: 0; transform: translateY(100vh) translateZ(0); }
          10% { opacity: 0.6; }
          90% { opacity: 0.6; }
          100% { opacity: 0; transform: translateY(-100vh) translateZ(0); }
        }

        /* Hero Aurora Blobs - Simplified for performance */
        .aurora-blob {
          position: absolute;
          filter: blur(80px); /* Reduced from 100px */
          border-radius: 50%;
          will-change: transform;
          backface-visibility: hidden;
          transform: translate3d(0, 0, 0); /* Force GPU layer */
          contain: layout style;
        }

        .blob-1 {
          top: -20%;
          right: -10%;
          width: 500px; /* Reduced size */
          height: 500px;
          background: linear-gradient(135deg, #ff00cc, #6600ff);
          opacity: 0.45;
          animation: blob-float-1 25s ease-in-out infinite; /* Slower = smoother */
        }

        .blob-2 {
          top: 30%;
          right: 20%;
          width: 350px; /* Reduced size */
          height: 350px;
          background: linear-gradient(135deg, #00ccff, #0066ff);
          opacity: 0.35;
          animation: blob-float-2 30s ease-in-out infinite; /* Slower = smoother */
        }

        /* Reduced from 4 blobs to 2 for performance */
        .blob-3,
        .blob-4 {
          display: none;
        }

        /* Simplified blob animations - only transform, no border-radius changes */
        @keyframes blob-float-1 {
          0%, 100% { transform: translateZ(0) translate(0, 0) scale(1); }
          50% { transform: translateZ(0) translate(-30px, 20px) scale(1.05); }
        }

        @keyframes blob-float-2 {
          0%, 100% { transform: translateZ(0) translate(0, 0) scale(1); }
          50% { transform: translateZ(0) translate(20px, -15px) scale(0.95); }
        }

        /* Remove old complex keyframes */
        @keyframes blob-morph-1 {
          0%, 100% { 
            transform: translateZ(0) translate(0, 0) scale(1);
            border-radius: 50% 50% 40% 60% / 40% 60% 50% 50%;
          }
          50% { 
            transform: translate(60px, -40px) scale(1.2) rotate(180deg);
            border-radius: 60% 40% 60% 40% / 50% 50% 50% 50%;
          }
        }

        @keyframes blob-morph-4 {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.5; }
          50% { transform: translate(30px, 30px) scale(1.3); opacity: 0.8; }
        }

        /* Glassmorphism - Optimized blur values */
        .glass-card {
          background: rgba(255, 255, 255, 0.12);
          backdrop-filter: blur(12px); /* Reduced from 20px */
          -webkit-backdrop-filter: blur(12px);
          border: 1px solid rgba(255, 255, 255, 0.18);
          box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.3);
          contain: layout paint;
          transform: translateZ(0);
        }

        .glass-card-light {
          background: rgba(255, 255, 255, 0.88);
          backdrop-filter: blur(8px); /* Reduced from 10px */
          border: 1px solid rgba(255, 255, 255, 0.5);
          contain: layout paint;
        }

        /* Features section aurora - Simplified */
        .features-blob {
          position: absolute;
          border-radius: 50%;
          filter: blur(60px); /* Reduced from 80px */
          contain: layout style;
          transform: translate3d(0, 0, 0);
        }

        .features-blob-1 {
          top: 10%;
          left: -10%;
          width: 350px; /* Reduced */
          height: 350px;
          background: rgba(147, 51, 234, 0.10);
          animation: features-pulse 20s ease-in-out infinite; /* Slower */
        }

        .features-blob-2 {
          bottom: 10%;
          right: -10%;
          width: 400px; /* Reduced */
          height: 400px;
          background: rgba(236, 72, 153, 0.08);
          animation: features-pulse 25s ease-in-out infinite; /* Slower */
          animation-delay: -10s;
        }

        /* Disabled third blob for performance */
        .features-blob-3 {
          display: none;
        }

        /* Single simplified animation for all feature blobs */
        @keyframes features-pulse {
          0%, 100% { opacity: 0.8; }
          50% { opacity: 1; }
        }

        /* Legacy keyframes kept for compatibility but simplified */
        @keyframes features-float-1 {
          0%, 100% { opacity: 0.8; }
          50% { opacity: 1; }
        }

        @keyframes features-float-2 {
          0%, 100% { opacity: 0.8; }
          50% { opacity: 1; }
        }

        @keyframes features-float-3 {
          0%, 100% { transform: translate(-50%, -50%); }
          50% { transform: translate(-40%, -60%) scale(1.5); }
        }

        /* Loading Bar Animation */
        .loading-bar {
          position: relative;
          overflow: hidden;
        }

        .loading-bar::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          height: 100%;
          width: 70%;
          background: linear-gradient(90deg, #6366f1, #ec4899, #f97316);
          border-radius: 9999px;
          animation: load 2s ease-in-out infinite;
          will-change: transform;
        }

        @keyframes load {
          0% { transform: translateX(-140%); }
          100% { transform: translateX(143%); }
        }

        /* Respect reduced motion preference */
        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }

        /* Feature Card */
        .feature-card {
          transition: all 0.4s ease;
          background: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(10px);
        }

        .feature-card:hover {
          transform: translateY(-8px) scale(1.02);
          box-shadow: 0 25px 50px -12px rgba(147, 51, 234, 0.25);
          border-color: #a855f7;
        }

        /* Dark Card */
        .dark-card {
          transition: all 0.3s ease;
        }

        .dark-card:hover {
          transform: translateY(-5px);
          border-color: rgba(139, 92, 246, 0.5);
          box-shadow: 0 0 30px rgba(139, 92, 246, 0.2);
        }

        /* Pricing Card Glow */
        .pricing-glow {
          position: relative;
        }

        .pricing-glow::before {
          content: '';
          position: absolute;
          inset: -2px;
          background: linear-gradient(45deg, #6366f1, #ec4899, #f97316, #6366f1);
          background-size: 400% 400%;
          border-radius: 1.5rem;
          z-index: -1;
          animation: gradient-rotate 4s linear infinite;
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .pricing-glow:hover::before {
          opacity: 1;
        }

        @keyframes gradient-rotate {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        /* Recommended card always glowing */
        .recommended-glow::before {
          content: '';
          position: absolute;
          inset: -3px;
          background: linear-gradient(45deg, #6366f1, #ec4899, #f97316, #6366f1);
          background-size: 400% 400%;
          border-radius: 1.5rem;
          z-index: -1;
          animation: gradient-rotate 3s linear infinite;
          opacity: 1;
        }

        /* Savings Bar */
        .savings-bar {
          position: relative;
          overflow: hidden;
        }

        .savings-bar::after {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
          animation: shimmer 2s infinite;
        }

        @keyframes shimmer {
          100% { left: 100%; }
        }

        /* Pulse Ring */
        .pulse-ring {
          position: relative;
        }

        .pulse-ring::before,
        .pulse-ring::after {
          content: '';
          position: absolute;
          inset: -4px;
          border: 2px solid rgba(147, 51, 234, 0.5);
          border-radius: 50%;
          animation: pulse-ring 2s ease-out infinite;
        }

        .pulse-ring::after {
          animation-delay: 1s;
        }

        @keyframes pulse-ring {
          0% { transform: scale(1); opacity: 1; }
          100% { transform: scale(1.5); opacity: 0; }
        }

        /* Floating Animation */
        .float-animation {
          animation: float 6s ease-in-out infinite;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(1deg); }
          50% { transform: translateY(-20px) rotate(-1deg); }
        }

        /* Grid Pattern */
        .grid-pattern {
          background-image: 
            linear-gradient(rgba(147, 51, 234, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(147, 51, 234, 0.03) 1px, transparent 1px);
          background-size: 50px 50px;
          animation: grid-move 20s linear infinite;
        }

        @keyframes grid-move {
          0% { background-position: 0 0; }
          100% { background-position: 50px 50px; }
        }

        /* Glow Text */
        .glow-text {
          text-shadow: 0 0 20px rgba(147, 51, 234, 0.5), 0 0 40px rgba(147, 51, 234, 0.3);
        }

        /* Button Shine */
        .btn-shine {
          position: relative;
          overflow: hidden;
        }

        .btn-shine::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
          animation: btn-shine 3s infinite;
        }

        @keyframes btn-shine {
          100% { left: 100%; }
        }

        /* Dark section aurora - Simplified for performance */
        .dark-blob {
          position: absolute;
          border-radius: 50%;
          filter: blur(80px); /* Reduced from 120px */
          contain: layout style;
          transform: translate3d(0, 0, 0);
        }

        .dark-blob-1 {
          top: -10%;
          left: 20%;
          width: 400px; /* Reduced */
          height: 400px;
          background: rgba(147, 51, 234, 0.20);
          animation: dark-pulse 25s ease-in-out infinite; /* Slower */
        }

        .dark-blob-2 {
          bottom: -10%;
          right: 20%;
          width: 350px; /* Reduced */
          height: 350px;
          background: rgba(236, 72, 153, 0.15);
          animation: dark-pulse 30s ease-in-out infinite; /* Slower */
          animation-delay: -12s;
        }

        /* Disabled third blob for performance */
        .dark-blob-3 {
          display: none;
        }

        @keyframes dark-pulse {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }

        /* Legacy keyframes for compatibility */
        @keyframes dark-float-1 { 0%, 100% { opacity: 0.6; } 50% { opacity: 1; } }
        @keyframes dark-float-2 { 0%, 100% { opacity: 0.6; } 50% { opacity: 1; } }
        @keyframes dark-float-3 { 0%, 100% { opacity: 0.6; } 50% { opacity: 1; } }

        /* Savings section aurora - Simplified */
        .savings-blob {
          position: absolute;
          border-radius: 50%;
          filter: blur(70px); /* Reduced from 100px */
          contain: layout style;
          transform: translate3d(0, 0, 0);
        }

        .savings-blob-1 {
          top: 10%;
          left: 10%;
          width: 300px; /* Reduced */
          height: 300px;
          background: rgba(59, 130, 246, 0.06);
          animation: savings-pulse 25s ease-in-out infinite; /* Slower */
        }

        .savings-blob-2 {
          bottom: 10%;
          right: 10%;
          width: 350px; /* Reduced */
          height: 350px;
          background: rgba(147, 51, 234, 0.06);
          animation: savings-pulse 30s ease-in-out infinite; /* Slower */
          animation-delay: -12s;
        }

        @keyframes savings-pulse {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 0.9; }
        }

        /* Legacy keyframes */
        @keyframes savings-float-1 { 0%, 100% { opacity: 0.6; } 50% { opacity: 0.9; } }
        @keyframes savings-float-2 { 0%, 100% { opacity: 0.6; } 50% { opacity: 0.9; } }

        /* Neon line - Optimized */
        .neon-line {
          position: absolute;
          height: 2px;
          background: linear-gradient(90deg, transparent, #8b5cf6, #ec4899, transparent);
          animation: neon-flow 4s linear infinite; /* Slower = smoother */
          will-change: transform;
          transform: translate3d(0, 0, 0);
        }

        @keyframes neon-flow {
          0% { transform: translate3d(-100%, 0, 0); }
          100% { transform: translate3d(100%, 0, 0); }
        }

        /* Hide scrollbar for pricing carousel */
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }

        /* Features scroll animation */
        @keyframes features-scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-features-scroll {
          animation: features-scroll 30s linear infinite;
          width: max-content;
        }
        .animate-features-scroll:hover {
          animation-play-state: paused;
        }

        .reveal-on-scroll {
          opacity: 0;
          transform: translateY(16px);
          transition: opacity 500ms ease, transform 500ms ease;
        }

        .reveal-on-scroll.is-visible {
          opacity: 1;
          transform: translateY(0);
        }

        @media (prefers-reduced-motion: reduce) {
          .reveal-on-scroll {
            opacity: 1;
            transform: none;
            transition: none;
          }
        }
      `}</style>

      {/* Global Aurora Background */}
      <div className="aurora-wrapper">
        <div className="aurora-layer aurora-gradient-1"></div>
        <div className="aurora-layer aurora-gradient-2"></div>
        <div className="aurora-layer aurora-gradient-3"></div>
        <div className="aurora-layer aurora-gradient-4"></div>
        <div className="aurora-layer aurora-gradient-5"></div>
      </div>

      {/* Floating Particles */}
      <div className="particles-container" id="particles"></div>

      {/* Navigation */}
      <nav className={`fixed w-full z-50 transition-all duration-500 ${scrolled ? 'bg-black/80 backdrop-blur-xl py-4' : 'bg-transparent py-6'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <Link href="/" className="flex items-center gap-3 group">
            <div className="w-10 h-10 rounded-xl overflow-hidden shadow-lg shadow-purple-500/30 ring-1 ring-purple-500/30 group-hover:ring-purple-400/50 group-hover:shadow-purple-400/40 transition-all duration-300">
              <Image src="/images/logo-purple.svg" alt="SHOWBOLD" width={40} height={40} priority className="w-full h-full" />
            </div>
            <span className="text-white font-bold text-xl tracking-tight">SHOWBOLD</span>
          </Link>
          <div className="hidden md:flex items-center space-x-8 text-sm font-medium text-gray-300">
            <a href="#features" className="hover:text-white transition-colors">Features</a>
            <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
            <div className="relative group">
              <button className="flex items-center gap-1 hover:text-white transition-colors">
                Resources <ChevronDown className="w-3 h-3" />
              </button>
            </div>
            <div className="relative group">
              <button className="flex items-center gap-1 hover:text-white transition-colors">
                Company <ChevronDown className="w-3 h-3" />
              </button>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Link href="/login" className="hidden sm:block bg-white/10 hover:bg-white/20 text-white px-5 py-2 rounded-lg text-sm font-medium transition-all border border-white/20 backdrop-blur-sm hover:border-purple-500/50">
              Log In
            </Link>
            <Link href="/signup" className="btn-shine bg-gradient-to-r from-orange-500 via-pink-500 to-purple-600 text-white px-5 py-2 rounded-lg text-sm font-medium shadow-lg shadow-purple-500/30">
              Get Started
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-24 md:pt-20 pb-8 overflow-hidden">
        {/* Hero Aurora */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="aurora-blob blob-1"></div>
          <div className="aurora-blob blob-2"></div>
          <div className="aurora-blob blob-3"></div>
          <div className="aurora-blob blob-4"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center py-12">
          {/* Left Content */}
          <div className="text-white space-y-6">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tight leading-tight glow-text">
              Your AI<br/>Marketing<br/>Engine.
            </h1>
            <p className="text-lg text-gray-300 max-w-lg leading-relaxed">
              Supercharge your content creation with intelligent automation and real-time insights. Build your brand faster, smarter, and without limits.
            </p>
            <div className="pt-4">
              <Link href="/signup" className="btn-shine bg-gradient-to-r from-orange-500 via-pink-500 to-purple-600 hover:from-orange-600 hover:via-pink-600 hover:to-purple-700 text-white px-8 py-4 rounded-full font-semibold text-lg shadow-lg shadow-purple-500/30 inline-flex items-center gap-3 transition-all transform hover:scale-105">
                Start Free Trial
                <Play className="w-5 h-5" />
              </Link>
            </div>
          </div>

          {/* Right Content: Interactive UI Card */}
          <div className="relative float-animation">
            <div className="glass-card rounded-2xl p-6 w-full max-w-md mx-auto">
              {/* Prompt Input */}
              <div className="mb-6">
                <label className="text-xs font-semibold text-white uppercase tracking-wider mb-2 block">Prompt</label>
                <div className="bg-white rounded-xl p-4 shadow-sm text-gray-800 text-sm font-medium">
                  Create a social post about our new summer collection.
                </div>
              </div>

              {/* Generating Status */}
              <div className="glass-card-light rounded-xl p-4 mb-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-600"></div>
                  <div>
                    <div className="text-sm font-bold text-gray-800">Generating...</div>
                    <div className="text-xs text-gray-500">3 variations</div>
                  </div>
                </div>
                <div className="h-2 bg-gray-200 rounded-full loading-bar"></div>
              </div>

              {/* Results Preview */}
              <div className="space-y-4">
                {/* Instagram */}
                <div className="glass-card-light rounded-xl p-3">
                  <div className="flex items-center gap-2 mb-2 text-xs font-bold text-gray-700">
                    <svg className="w-5 h-5 text-pink-600" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                    Instagram
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <img src="https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=200&h=200&fit=crop" alt="Product mockup 1" className="rounded-lg h-20 w-full object-cover" />
                    <img src="https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=200&h=200&fit=crop" alt="Product mockup 2" className="rounded-lg h-20 w-full object-cover" />
                    <img src="https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=200&h=200&fit=crop" alt="Product mockup 3" className="rounded-lg h-20 w-full object-cover" />
                  </div>
                </div>

                {/* LinkedIn */}
                <div className="glass-card-light rounded-xl p-3 flex gap-3 items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1 text-xs font-bold text-gray-700">
                      <svg className="w-5 h-5 text-blue-700" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                      LinkedIn
                    </div>
                    <p className="text-[10px] text-gray-600 leading-snug">
                      Excited to announce our new summer collection! Fresh styles, vibrant colors...
                    </p>
                  </div>
                  <img src="https://images.unsplash.com/photo-1560769629-975ec94e6a86?w=100&h=100&fit=crop" alt="Product" className="rounded-lg w-14 h-14 object-cover" />
                </div>

                {/* Twitter/X */}
                <div className="glass-card-light rounded-xl p-3">
                  <div className="flex items-center gap-2 mb-1 text-xs font-bold text-gray-700">
                    <svg className="w-4 h-4 text-black" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                    X (Twitter)
                  </div>
                  <p className="text-[10px] text-gray-600 leading-snug">
                    Summer vibes incoming ☀️ Our new collection just dropped! #SummerStyle #NewArrivals
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 relative overflow-hidden reveal-on-scroll">
        {/* Features Aurora Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="features-blob features-blob-1"></div>
          <div className="features-blob features-blob-2"></div>
          <div className="features-blob features-blob-3"></div>
        </div>

        {/* Grid Pattern */}
        <div className="absolute inset-0 grid-pattern"></div>

        {/* Content Background */}
        <div className="absolute inset-0 bg-white/90 backdrop-blur-sm"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900">AI-Powered Growth, Redefined.</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Feature 1 */}
            <div className="feature-card border border-gray-200 rounded-3xl p-8 relative group pricing-glow">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center mb-6">
                <Palette className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Content Creation</h3>
              <p className="text-gray-600 text-lg">Instantly generate high-quality copy, images, and videos across platforms.</p>
            </div>

            {/* Feature 2 */}
            <div className="feature-card border border-gray-200 rounded-3xl p-8 pricing-glow">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-100 to-yellow-100 flex items-center justify-center mb-6">
                <Calendar className="w-8 h-8 text-orange-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Smart Scheduling</h3>
              <p className="text-gray-600 text-lg">Automate publishing times for maximum engagement.</p>
            </div>

            {/* Feature 3 */}
            <div className="feature-card border border-gray-200 rounded-3xl p-8 pricing-glow">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-pink-100 to-red-100 flex items-center justify-center mb-6">
                <BarChart3 className="w-8 h-8 text-pink-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Performance Analytics</h3>
              <p className="text-gray-600 text-lg">Track and optimize campaigns with real-time data.</p>
            </div>

            {/* Feature 4 */}
            <div className="feature-card border border-gray-200 rounded-3xl p-8 pricing-glow">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center mb-6">
                <Mic2 className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Brand Voice AI</h3>
              <p className="text-gray-600 text-lg">Maintain consistent brand tone and messaging.</p>
            </div>
          </div>

          {/* Logos */}
          <div className="mt-24 text-center">
            <h4 className="text-xl font-semibold text-gray-900 mb-10">Trusted by Industry Leaders</h4>
            <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12">
              <div className="flex items-center gap-2 text-xl font-bold text-gray-500 hover:text-gray-800 transition-colors">
                <Building2 className="w-6 h-6" /> Acme Corp
              </div>
              <div className="flex items-center gap-2 text-xl font-bold text-gray-500 hover:text-gray-800 transition-colors">
                <Zap className="w-6 h-6" /> TechFlow
              </div>
              <div className="flex items-center gap-2 text-xl font-bold text-gray-500 hover:text-gray-800 transition-colors">
                <Flower2 className="w-6 h-6" /> Bloom
              </div>
              <div className="flex items-center gap-2 text-xl font-bold text-gray-500 hover:text-gray-800 transition-colors">
                <Package className="w-6 h-6" /> NextGen
              </div>
              <div className="flex items-center gap-2 text-xl font-bold text-blue-500 hover:text-blue-600 transition-colors">
                <CheckCircle className="w-6 h-6" /> Velocity
              </div>
              <div className="flex items-center gap-2 text-xl font-bold text-gray-500 hover:text-gray-800 transition-colors">
                <Hexagon className="w-6 h-6" /> Pinnacle
              </div>
            </div>
          </div>

          {/* 200+ Features Carousel */}
          <div className="mt-24">
            <div className="text-center mb-12">
              <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">200+ Features, One Platform</h3>
              <p className="text-gray-600 text-lg">Everything you need to dominate social media</p>
            </div>
            <div className="overflow-hidden relative">
              <div className="flex gap-4 animate-features-scroll">
                {[
                  "AI Captions", "Hashtag Generator", "Mockup Studio", "4K Upscaling", "Background Removal", 
                  "Video Creator", "Voiceover Studio", "Auto-Scheduling", "Best Time to Post", "Content Calendar",
                  "Multi-Platform", "Instagram", "TikTok", "LinkedIn", "X/Twitter", "YouTube", "Pinterest", "Threads",
                  "AI Coach", "Competitor Analysis", "Trend Detection", "Viral Predictor", "A/B Testing",
                  "Team Collaboration", "Approval Workflows", "Client Workspaces", "White-Label", "API Access",
                  "Brand Voice", "Tone Matching", "Content Ideas", "Headlines Generator", "Script Writer",
                  "Analytics Dashboard", "ROI Tracking", "Engagement Metrics", "Follower Growth", "Performance Reports",
                  "Media Library", "Asset Management", "Templates", "Presets", "Filters",
                  "Autopilot Mode", "Auto-Like", "Auto-Comment", "Cross-Posting", "RSS Feeds",
                  "Image Generation", "Video Generation", "AI Thumbnails", "Caption AI", "Stories AI",
                  "AI Captions", "Hashtag Generator", "Mockup Studio", "4K Upscaling", "Background Removal", 
                  "Video Creator", "Voiceover Studio", "Auto-Scheduling", "Best Time to Post", "Content Calendar"
                ].map((feature, i) => (
                  <div key={i} className="flex-shrink-0 px-6 py-3 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-100 rounded-full text-sm font-medium text-gray-700 whitespace-nowrap hover:from-purple-100 hover:to-pink-100 transition-colors">
                    {feature}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dark Mode Features / Pricing Section */}
      <section id="pricing" className="py-24 relative overflow-hidden bg-[#050511] reveal-on-scroll">
        {/* Dark Aurora Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="dark-blob dark-blob-1"></div>
          <div className="dark-blob dark-blob-2"></div>
          <div className="dark-blob dark-blob-3"></div>
        </div>

        {/* Animated Grid Lines */}
        <div className="absolute inset-0 opacity-10" style={{backgroundImage: 'linear-gradient(rgba(147, 51, 234, 0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(147, 51, 234, 0.5) 1px, transparent 1px)', backgroundSize: '60px 60px'}}></div>

        {/* Neon Lines */}
        <div className="neon-line absolute top-1/4 left-0 right-0"></div>
        <div className="neon-line absolute top-3/4 left-0 right-0" style={{animationDelay: '-1.5s'}}></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 glow-text">Your AI Marketing Team</h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Leverage powerful, data-driven insights for accelerating growth. Start building today.
            </p>
            <Link href="/signup" className="mt-8 inline-block btn-shine bg-purple-600 hover:bg-purple-700 text-white px-8 py-3 rounded-full font-medium transition-all shadow-[0_0_30px_rgba(147,51,234,0.5)] hover:shadow-[0_0_50px_rgba(147,51,234,0.7)]">
              Get Started
            </Link>
          </div>

          {/* Dark Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-32">
            <div className="dark-card bg-[#0f172a]/80 backdrop-blur-xl border border-gray-800 rounded-2xl p-6">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-900 to-purple-700 rounded-lg flex items-center justify-center mb-4">
                <BookOpen className="w-6 h-6 text-purple-300" />
              </div>
              <h3 className="text-white font-bold text-lg mb-2">Intelligent Content</h3>
              <p className="text-gray-400 text-sm">AI-powered content generation with brand consistency.</p>
            </div>
            <div className="dark-card bg-[#0f172a]/80 backdrop-blur-xl border border-gray-800 rounded-2xl p-6">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-900 to-blue-700 rounded-lg flex items-center justify-center mb-4">
                <PieChart className="w-6 h-6 text-blue-300" />
              </div>
              <h3 className="text-white font-bold text-lg mb-2">Data Analytics</h3>
              <p className="text-gray-400 text-sm">Leverage powerful, data-driven insights for growth.</p>
            </div>
            <div className="dark-card bg-[#0f172a]/80 backdrop-blur-xl border border-gray-800 rounded-2xl p-6">
              <div className="w-12 h-12 bg-gradient-to-br from-pink-900 to-pink-700 rounded-lg flex items-center justify-center mb-4">
                <Rocket className="w-6 h-6 text-pink-300" />
              </div>
              <h3 className="text-white font-bold text-lg mb-2">Automated Campaigns</h3>
              <p className="text-gray-400 text-sm">Set up once, run forever with smart automation.</p>
            </div>
            <div className="dark-card bg-[#0f172a]/80 backdrop-blur-xl border border-gray-800 rounded-2xl p-6">
              <div className="w-12 h-12 bg-gradient-to-br from-green-900 to-green-700 rounded-lg flex items-center justify-center mb-4">
                <Search className="w-6 h-6 text-green-300" />
              </div>
              <h3 className="text-white font-bold text-lg mb-2">Predictive Insights</h3>
              <p className="text-gray-400 text-sm">Forecast trends and optimize before competitors.</p>
            </div>
            <div className="dark-card bg-[#0f172a]/80 backdrop-blur-xl border border-gray-800 rounded-2xl p-6">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-900 to-orange-700 rounded-lg flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-orange-300" />
              </div>
              <h3 className="text-white font-bold text-lg mb-2">Audience Segmentation</h3>
              <p className="text-gray-400 text-sm">Target the right audience at the right time.</p>
            </div>
            <div className="dark-card bg-[#0f172a]/80 backdrop-blur-xl border border-gray-800 rounded-2xl p-6">
              <div className="w-12 h-12 bg-gradient-to-br from-cyan-900 to-cyan-700 rounded-lg flex items-center justify-center mb-4">
                <TrendingUp className="w-6 h-6 text-cyan-300" />
              </div>
              <h3 className="text-white font-bold text-lg mb-2">Performance Tracking</h3>
              <p className="text-gray-400 text-sm">Real-time metrics and actionable reports.</p>
            </div>
          </div>

          {/* Pricing */}
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white glow-text">Simple, Transparent Pricing</h2>
            <p className="text-gray-400 mt-2">Choose the plan that fits your business needs.</p>
            
            {/* Billing Toggle */}
            <div className="flex items-center justify-center gap-4 mt-8">
              <span className={`text-sm font-medium transition-colors ${!isAnnual ? 'text-white' : 'text-gray-500'}`}>Monthly</span>
              <button
                onClick={() => setIsAnnual(!isAnnual)}
                aria-label={isAnnual ? 'Switch to monthly billing' : 'Switch to annual billing'}
                role="switch"
                aria-checked={isAnnual}
                className={`relative w-16 h-8 rounded-full transition-all duration-300 ${isAnnual ? 'bg-gradient-to-r from-purple-600 to-pink-500' : 'bg-gray-700'}`}
              >
                <div className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow-lg transition-all duration-300 ${isAnnual ? 'left-9' : 'left-1'}`} />
              </button>
              <span className={`text-sm font-medium transition-colors ${isAnnual ? 'text-white' : 'text-gray-500'}`}>
                Annual
                <span className="ml-2 px-2 py-0.5 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs font-bold rounded-full">
                  20% OFF
                </span>
              </span>
            </div>
          </div>

          {/* Pricing Carousel */}
          <div className="overflow-x-auto pb-8 pt-6 -mx-4 px-4 scrollbar-hide">
            <div className="flex gap-6 min-w-max">
              {/* FREE */}
              <div className="bg-[#0f172a]/80 backdrop-blur-xl border border-gray-800 rounded-2xl p-6 flex flex-col w-72 flex-shrink-0 pricing-glow">
                <h3 className="text-xl font-bold text-white mb-2">Free</h3>
                <p className="text-gray-400 text-sm mb-4">Try before you buy</p>
                <div className="text-4xl font-bold text-white mb-2">$0<span className="text-lg font-normal text-gray-400">/month</span></div>
                <p className="text-xs text-gray-500 mb-4">Forever free</p>
                <ul className="space-y-2 mb-6 text-sm text-gray-300 flex-1">
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0" /> 2 social accounts</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0" /> 10 posts/month</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0" /> 10 AI credits/month</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0" /> 5 mockups/month</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0" /> Basic templates</li>
                </ul>
                <Link href="/signup?plan=free" className="w-full border border-gray-600 text-white py-3 rounded-xl hover:bg-white/10 transition-all font-medium text-center block">
                  Get Started
                </Link>
              </div>

              {/* STARTER */}
              <div className="bg-[#0f172a]/80 backdrop-blur-xl border border-gray-800 rounded-2xl p-6 flex flex-col w-72 flex-shrink-0 pricing-glow">
                <h3 className="text-xl font-bold text-white mb-2">Starter</h3>
                <p className="text-gray-400 text-sm mb-4">For individuals</p>
                <div className="mb-2">
                  {isAnnual ? (
                    <>
                      <span className="text-lg text-gray-500 line-through mr-2">$19</span>
                      <span className="text-4xl font-bold text-white">$15</span>
                      <span className="text-lg font-normal text-gray-400">/month</span>
                    </>
                  ) : (
                    <>
                      <span className="text-4xl font-bold text-white">$19</span>
                      <span className="text-lg font-normal text-gray-400">/month</span>
                    </>
                  )}
                </div>
                <p className="text-xs text-gray-500 mb-4">{isAnnual ? 'Billed annually ($180/yr)' : '$15/mo billed annually'}</p>
                <ul className="space-y-2 mb-6 text-sm text-gray-300 flex-1">
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0" /> 5 social accounts</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0" /> 60 posts/month</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0" /> 100 AI credits/month</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0" /> 50 mockups/month</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0" /> 10 AI images</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0" /> 30-day analytics</li>
                </ul>
                <Link href="/signup?plan=starter" className="w-full border border-gray-600 text-white py-3 rounded-xl hover:bg-white/10 transition-all font-medium text-center block">
                  Start Free Trial
                </Link>
              </div>

              {/* PRO - Most Popular */}
              <div className="relative recommended-glow bg-[#0f172a] rounded-2xl p-6 pt-8 flex flex-col w-72 flex-shrink-0">
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-purple-600 via-pink-500 to-orange-500 px-4 py-1.5 rounded-full text-xs font-bold text-white uppercase tracking-wider whitespace-nowrap shadow-lg">
                  ⭐ MOST POPULAR
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Pro</h3>
                <p className="text-gray-400 text-sm mb-4">For creators & teams</p>
                <div className="mb-2">
                  {isAnnual ? (
                    <>
                      <span className="text-lg text-gray-500 line-through mr-2">$49</span>
                      <span className="text-4xl font-bold text-white">$39</span>
                      <span className="text-lg font-normal text-gray-400">/month</span>
                    </>
                  ) : (
                    <>
                      <span className="text-4xl font-bold text-white">$49</span>
                      <span className="text-lg font-normal text-gray-400">/month</span>
                    </>
                  )}
                </div>
                <p className="text-xs text-gray-500 mb-4">{isAnnual ? 'Billed annually ($468/yr)' : '$39/mo billed annually'}</p>
                <ul className="space-y-2 mb-6 text-sm text-gray-300 flex-1">
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0" /> 15 social accounts</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0" /> 300 posts/month</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0" /> 500 AI credits/month</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0" /> 300 mockups/month</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0" /> 50 AI images</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0" /> 40 AI videos</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0" /> Autopilot mode</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0" /> 3 team members</li>
                </ul>
                <Link href="/signup?plan=pro" className="w-full btn-shine bg-gradient-to-r from-purple-600 via-pink-500 to-orange-500 text-white py-3 rounded-xl hover:opacity-90 transition-all font-bold shadow-lg text-center block">
                  Start Free Trial
                </Link>
              </div>

              {/* PRO+ - Best Value */}
              <div className="relative bg-[#0f172a] border-2 border-emerald-500/50 rounded-2xl p-6 pt-8 flex flex-col w-72 flex-shrink-0 pricing-glow">
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-emerald-500 to-teal-500 px-4 py-1.5 rounded-full text-xs font-bold text-white uppercase tracking-wider whitespace-nowrap shadow-lg">
                  💎 BEST VALUE
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Pro+</h3>
                <p className="text-gray-400 text-sm mb-4">For power users</p>
                <div className="mb-2">
                  {isAnnual ? (
                    <>
                      <span className="text-lg text-gray-500 line-through mr-2">$99</span>
                      <span className="text-4xl font-bold text-white">$79</span>
                      <span className="text-lg font-normal text-gray-400">/month</span>
                    </>
                  ) : (
                    <>
                      <span className="text-4xl font-bold text-white">$99</span>
                      <span className="text-lg font-normal text-gray-400">/month</span>
                    </>
                  )}
                </div>
                <p className="text-xs text-gray-500 mb-4">{isAnnual ? 'Billed annually ($948/yr)' : '$79/mo billed annually'}</p>
                <ul className="space-y-2 mb-6 text-sm text-gray-300 flex-1">
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0" /> 20 social accounts</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0" /> 1,000 posts/month</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0" /> 1,000 AI credits/month</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0" /> 150 AI images</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0" /> 156 video credits</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0" /> AI Marketing Coach</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0" /> 10 competitor tracking</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0" /> 5 team members</li>
                </ul>
                <Link href="/signup?plan=pro_plus" className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-3 rounded-xl hover:opacity-90 transition-all font-bold shadow-lg text-center block">
                  Start Free Trial
                </Link>
              </div>

              {/* BUSINESS */}
              <div className="bg-[#0f172a]/80 backdrop-blur-xl border border-gray-800 rounded-2xl p-6 flex flex-col w-72 flex-shrink-0 pricing-glow">
                <h3 className="text-xl font-bold text-white mb-2">Business</h3>
                <p className="text-gray-400 text-sm mb-4">For growing businesses</p>
                <div className="mb-2">
                  {isAnnual ? (
                    <>
                      <span className="text-lg text-gray-500 line-through mr-2">$149</span>
                      <span className="text-4xl font-bold text-white">$119</span>
                      <span className="text-lg font-normal text-gray-400">/month</span>
                    </>
                  ) : (
                    <>
                      <span className="text-4xl font-bold text-white">$149</span>
                      <span className="text-lg font-normal text-gray-400">/month</span>
                    </>
                  )}
                </div>
                <p className="text-xs text-gray-500 mb-4">{isAnnual ? 'Billed annually ($1,428/yr)' : '$119/mo billed annually'}</p>
                <ul className="space-y-2 mb-6 text-sm text-gray-300 flex-1">
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0" /> 50 social accounts</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0" /> 1,000 posts/month</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0" /> 2,000 AI credits/month</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0" /> 200 AI images</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0" /> AI Marketing Coach</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0" /> 5 competitor tracking</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0" /> 10 team members</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0" /> API access</li>
                </ul>
                <Link href="/signup?plan=business" className="w-full border border-gray-600 text-white py-3 rounded-xl hover:bg-white/10 transition-all font-medium text-center block">
                  Start Free Trial
                </Link>
              </div>

              {/* AGENCY */}
              <div className="bg-[#0f172a]/80 backdrop-blur-xl border border-gray-800 rounded-2xl p-6 flex flex-col w-72 flex-shrink-0 pricing-glow">
                <h3 className="text-xl font-bold text-white mb-2">Agency</h3>
                <p className="text-gray-400 text-sm mb-4">For agencies & studios</p>
                <div className="mb-2">
                  {isAnnual ? (
                    <>
                      <span className="text-lg text-gray-500 line-through mr-2">$299</span>
                      <span className="text-4xl font-bold text-white">$239</span>
                      <span className="text-lg font-normal text-gray-400">/month</span>
                    </>
                  ) : (
                    <>
                      <span className="text-4xl font-bold text-white">$299</span>
                      <span className="text-lg font-normal text-gray-400">/month</span>
                    </>
                  )}
                </div>
                <p className="text-xs text-gray-500 mb-4">{isAnnual ? 'Billed annually ($2,868/yr)' : '$239/mo billed annually'}</p>
                <ul className="space-y-2 mb-6 text-sm text-gray-300 flex-1">
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0" /> 150 social accounts</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0" /> 5,000 posts/month</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0" /> 10,000 AI credits</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0" /> 1,000 AI images</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0" /> 50 client workspaces</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0" /> White-label option</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0" /> 25 team members</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0" /> Client portal</li>
                </ul>
                <Link href="/signup?plan=agency" className="w-full border border-gray-600 text-white py-3 rounded-xl hover:bg-white/10 transition-all font-medium text-center block">
                  Start Free Trial
                </Link>
              </div>

              {/* ENTERPRISE */}
              <div className="bg-[#0f172a]/80 backdrop-blur-xl border border-gray-800 rounded-2xl p-6 flex flex-col w-72 flex-shrink-0 pricing-glow">
                <h3 className="text-xl font-bold text-white mb-2">Enterprise</h3>
                <p className="text-gray-400 text-sm mb-4">For large organizations</p>
                <div className="mb-2">
                  {isAnnual ? (
                    <>
                      <span className="text-lg text-gray-500 line-through mr-2">$499</span>
                      <span className="text-4xl font-bold text-white">$399</span>
                      <span className="text-lg font-normal text-gray-400">/month</span>
                    </>
                  ) : (
                    <>
                      <span className="text-4xl font-bold text-white">$499</span>
                      <span className="text-lg font-normal text-gray-400">/month</span>
                    </>
                  )}
                </div>
                <p className="text-xs text-gray-500 mb-4">{isAnnual ? 'Billed annually ($4,788/yr)' : '$399/mo billed annually'}</p>
                <ul className="space-y-2 mb-6 text-sm text-gray-300 flex-1">
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0" /> 500 social accounts</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0" /> 15,000 posts/month</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0" /> 25,000 AI credits</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0" /> Unlimited mockups</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0" /> SSO/SAML</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0" /> 99.9% SLA</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0" /> 100 team members</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0" /> Dedicated success manager</li>
                </ul>
                <Link href="/contact" className="w-full border border-gray-600 text-white py-3 rounded-xl hover:bg-white/10 transition-all font-medium text-center block">
                  Contact Sales
                </Link>
              </div>
            </div>
          </div>
          <p className="text-center text-gray-500 text-sm mt-4">← Scroll to see all plans →</p>
        </div>
      </section>

      {/* Savings & Integration Section */}
      <section className="py-24 relative overflow-hidden reveal-on-scroll">
        {/* Savings Aurora Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="savings-blob savings-blob-1"></div>
          <div className="savings-blob savings-blob-2"></div>
        </div>

        {/* Content Background */}
        <div className="absolute inset-0 bg-white/95 backdrop-blur-sm"></div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Platform Comparison:<br/>Savings Dashboard</h2>
          </div>

          {/* Savings Chart */}
          <div className="bg-gradient-to-br from-gray-50 via-white to-purple-50 p-8 md:p-12 rounded-3xl shadow-2xl border border-purple-100 mb-24 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-80 h-80 bg-gradient-to-br from-blue-200 to-purple-200 rounded-full filter blur-3xl opacity-15"></div>
            <div className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-br from-pink-200 to-orange-200 rounded-full filter blur-3xl opacity-15"></div>

            <div className="relative z-10 space-y-10">
              {/* Traditional */}
              <div>
                <div className="flex justify-between mb-3 text-sm font-semibold text-gray-700">
                  <span>Traditional Marketing Team</span>
                  <span className="text-gray-500">5+ employees needed</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex-1 h-14 bg-gray-200 rounded-xl overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-red-500 via-red-400 to-orange-400 rounded-xl savings-bar" style={{width: '100%'}}></div>
                  </div>
                  <span className="font-bold text-gray-900 text-xl whitespace-nowrap">$300,000/yr</span>
                </div>
              </div>

              {/* SHOWBOLD */}
              <div>
                <div className="flex justify-between mb-3 text-sm font-semibold text-gray-700">
                  <span>SHOWBOLD Growth Plan</span>
                  <span className="text-green-600">1 subscription</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex-1 h-14 bg-gray-200 rounded-xl overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-purple-600 via-pink-500 to-cyan-400 rounded-xl savings-bar" style={{width: '12%'}}></div>
                  </div>
                  <span className="font-bold text-gray-900 text-xl whitespace-nowrap">$1,188/yr</span>
                </div>
              </div>

              <div className="text-center pt-8 border-t border-gray-200">
                <div className="inline-block bg-gradient-to-r from-green-100 to-emerald-100 px-8 py-4 rounded-2xl border border-green-200">
                  <p className="text-sm text-green-700 font-medium mb-1">Annual Savings</p>
                  <h3 className="text-4xl md:text-5xl font-bold text-green-600">$298,812/yr</h3>
                </div>
              </div>
            </div>
          </div>

          {/* Integration Hub */}
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">Integration Hub</h2>
            <p className="text-gray-600 mt-2">Connect with 18+ social platforms</p>
          </div>

          <div className="relative bg-gradient-to-br from-gray-50 to-purple-50 rounded-3xl p-8 md:p-12 border border-purple-100 shadow-lg">
            {/* Status Badge */}
            <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-white shadow-xl rounded-xl px-6 py-3 flex items-center gap-3 border border-green-100">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center text-green-500">
                <Check className="w-5 h-5" />
              </div>
              <div className="text-left">
                <div className="text-xs text-gray-500">Platforms Connected:</div>
                <div className="text-sm font-bold text-green-600 flex items-center gap-2">
                  18 Integrations
                </div>
              </div>
            </div>

            {/* Social Media Logos Carousel */}
            {(() => {
              const platforms = [
                { name: 'Facebook', color: '#1877F2', icon: <svg viewBox="0 0 24 24" className="w-8 h-8" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg> },
                { name: 'Instagram', color: '#E4405F', icon: <svg viewBox="0 0 24 24" className="w-8 h-8" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg> },
                { name: 'X', color: '#000000', icon: <svg viewBox="0 0 24 24" className="w-8 h-8" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg> },
                { name: 'LinkedIn', color: '#0A66C2', icon: <svg viewBox="0 0 24 24" className="w-8 h-8" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg> },
                { name: 'TikTok', color: '#000000', icon: <svg viewBox="0 0 24 24" className="w-8 h-8" fill="currentColor"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg> },
                { name: 'YouTube', color: '#FF0000', icon: <svg viewBox="0 0 24 24" className="w-8 h-8" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg> },
                { name: 'Pinterest', color: '#E60023', icon: <svg viewBox="0 0 24 24" className="w-8 h-8" fill="currentColor"><path d="M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 01.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12 0-6.628-5.373-12-12-12z"/></svg> },
                { name: 'Threads', color: '#000000', icon: <svg viewBox="0 0 24 24" className="w-8 h-8" fill="currentColor"><path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.59 12c.025 3.086.718 5.496 2.057 7.164 1.43 1.783 3.631 2.698 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.31-.71-.873-1.3-1.634-1.75-.192 1.352-.622 2.446-1.284 3.272-.886 1.102-2.14 1.704-3.73 1.79-1.202.065-2.361-.218-3.259-.801-1.063-.689-1.685-1.74-1.752-2.96-.065-1.182.408-2.256 1.33-3.022.88-.73 2.082-1.146 3.39-1.17 1.14-.02 2.201.163 3.156.54-.044-.796-.216-1.418-.513-1.848-.396-.57-1.049-.856-1.942-.852-.774.003-1.399.234-1.857.687-.442.437-.708 1.041-.789 1.796l-2.093-.275c.129-1.202.58-2.198 1.342-2.96.868-.868 2.027-1.337 3.448-1.394 1.498-.058 2.695.35 3.558 1.212.769.77 1.245 1.86 1.417 3.244.593.284 1.133.636 1.61 1.055.95.833 1.612 1.934 1.91 3.182.34 1.423.238 3.132-.972 4.676-1.722 2.195-4.425 3.058-7.792 3.086zM9.72 15.913c.063 1.139 1.02 1.916 2.524 1.834 1.076-.058 1.855-.42 2.386-1.108.397-.513.67-1.174.819-1.982-1.015-.513-2.19-.752-3.464-.705-1.602.061-2.357.85-2.265 1.961z"/></svg> },
                { name: 'Snapchat', color: '#FFFC00', icon: <svg viewBox="0 0 24 24" className="w-8 h-8" fill="currentColor"><path d="M12.206.793c.99 0 4.347.276 5.93 3.821.529 1.193.403 3.219.299 4.847l-.003.06c-.012.18-.022.345-.03.51.075.045.203.09.401.09.3-.016.659-.12 1.033-.301a.603.603 0 01.257-.052c.156 0 .324.049.458.144.284.193.39.537.259.868-.172.419-.659.619-1.075.619-.156 0-.298-.024-.438-.048-.09-.016-.18-.032-.27-.032-.152 0-.324.024-.458.072-.18.065-.34.193-.434.376a.613.613 0 01-.54.322c-.18 0-.364-.076-.49-.209a.618.618 0 01-.181-.49c.008-.18.083-.357.22-.483a.89.89 0 01.633-.276c.094 0 .189.016.283.048.15.049.3.088.45.088.067 0 .134-.008.2-.024-.021-.364-.098-.728-.222-1.073a.574.574 0 01-.053-.193c0-.322.262-.583.584-.583a.58.58 0 01.553.403c.143.417.241.853.29 1.3.008.069.021.138.038.206.021.082.045.164.053.25v.015c.015.19-.045.386-.164.539a.722.722 0 01-.164.157c.135.18.279.35.427.51.66.705 1.526 1.193 2.505 1.412.09.021.18.045.27.075a.715.715 0 01.48.693.705.705 0 01-.315.578 1.73 1.73 0 01-.629.27c-.478.09-.927.337-1.335.734-.298.29-.509.634-.659.997a4.3 4.3 0 01-.346.623.612.612 0 01-.517.284c-.075 0-.15-.016-.218-.045l-.006-.003c-.48-.18-.997-.283-1.518-.299a4.63 4.63 0 00-.63.015 4.23 4.23 0 00-1.335.322c-.45.193-.868.478-1.193.847-.45.51-.814 1.117-1.08 1.796a.612.612 0 01-.573.39h-.008a.61.61 0 01-.564-.375c-.27-.68-.63-1.284-1.08-1.796a3.28 3.28 0 00-1.193-.847 4.23 4.23 0 00-1.335-.322 4.63 4.63 0 00-.63-.015c-.52.016-1.037.119-1.518.299l-.006.003a.608.608 0 01-.735-.239.61.61 0 01.004-.619c.12-.217.254-.42.394-.608.15-.363.361-.707.659-.997.408-.397.857-.644 1.335-.734a1.73 1.73 0 00.629-.27.705.705 0 00.315-.578.715.715 0 00-.48-.693 2.44 2.44 0 01-.27-.075c-.979-.22-1.845-.707-2.505-1.412a4.53 4.53 0 01-.427-.51.722.722 0 00-.164-.157.696.696 0 01-.164-.539v-.015c.008-.086.032-.168.053-.25.017-.068.03-.137.038-.206.049-.447.147-.883.29-1.3a.58.58 0 01.553-.403.574.574 0 01.584.583.574.574 0 01-.053.193c-.124.345-.201.709-.222 1.073.066.016.133.024.2.024.15 0 .3-.039.45-.088.094-.032.189-.048.283-.048a.89.89 0 01.633.276c.137.126.212.303.22.483a.618.618 0 01-.181.49.626.626 0 01-.49.209.613.613 0 01-.54-.322 .667.667 0 00-.434-.376.742.742 0 00-.458-.072c-.09 0-.18.016-.27.032-.14.024-.282.048-.438.048-.416 0-.903-.2-1.075-.619-.131-.331-.025-.675.259-.868a.603.603 0 01.458-.144c.09 0 .176.016.257.052.374.181.733.285 1.033.301.198 0 .326-.045.401-.09a7.76 7.76 0 01-.03-.51l-.003-.06c-.104-1.628-.23-3.654.299-4.847C7.859 1.07 11.216.793 12.206.793"/></svg> },
                { name: 'Reddit', color: '#FF4500', icon: <svg viewBox="0 0 24 24" className="w-8 h-8" fill="currentColor"><path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/></svg> },
                { name: 'Discord', color: '#5865F2', icon: <svg viewBox="0 0 24 24" className="w-8 h-8" fill="currentColor"><path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z"/></svg> },
                { name: 'Telegram', color: '#26A5E4', icon: <svg viewBox="0 0 24 24" className="w-8 h-8" fill="currentColor"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg> },
                { name: 'WhatsApp', color: '#25D366', icon: <svg viewBox="0 0 24 24" className="w-8 h-8" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg> },
                { name: 'Bluesky', color: '#0085FF', icon: <svg viewBox="0 0 24 24" className="w-8 h-8" fill="currentColor"><path d="M12 10.8c-1.087-2.114-4.046-6.053-6.798-7.995C2.566.944 1.561 1.266.902 1.565.139 1.908 0 3.08 0 3.768c0 .69.378 5.65.624 6.479.815 2.736 3.713 3.66 6.383 3.364.136-.02.275-.039.415-.056-.138.022-.276.04-.415.056-3.912.58-7.387 2.005-2.83 7.078 5.013 5.19 6.87-1.113 7.823-4.308.953 3.195 2.05 9.271 7.733 4.308 4.267-4.308 1.172-6.498-2.74-7.078a8.741 8.741 0 01-.415-.056c.14.017.279.036.415.056 2.67.297 5.568-.628 6.383-3.364.246-.828.624-5.79.624-6.478 0-.69-.139-1.861-.902-2.206-.659-.298-1.664-.62-4.3 1.24C16.046 4.748 13.087 8.687 12 10.8Z"/></svg> },
                { name: 'Mastodon', color: '#6364FF', icon: <svg viewBox="0 0 24 24" className="w-8 h-8" fill="currentColor"><path d="M23.268 5.313c-.35-2.578-2.617-4.61-5.304-5.004C17.51.242 15.792 0 11.813 0h-.03c-3.98 0-4.835.242-5.288.309C3.882.692 1.496 2.518.917 5.127.64 6.412.61 7.837.661 9.143c.074 1.874.088 3.745.26 5.611.118 1.24.325 2.47.62 3.68.55 2.237 2.777 4.098 4.96 4.857 2.336.792 4.849.923 7.256.38.265-.061.527-.132.786-.213.585-.184 1.27-.39 1.774-.753a.057.057 0 0 0 .023-.043v-1.809a.052.052 0 0 0-.02-.041.053.053 0 0 0-.046-.01 20.282 20.282 0 0 1-4.709.545c-2.73 0-3.463-1.284-3.674-1.818a5.593 5.593 0 0 1-.319-1.433.053.053 0 0 1 .066-.054c1.517.363 3.072.546 4.632.546.376 0 .75 0 1.125-.01 1.57-.044 3.224-.124 4.768-.422.038-.008.077-.015.11-.024 2.435-.464 4.753-1.92 4.989-5.604.008-.145.03-1.52.03-1.67.002-.512.167-3.63-.024-5.545zm-3.748 9.195h-2.561V8.29c0-1.309-.55-1.976-1.67-1.976-1.23 0-1.846.79-1.846 2.35v3.403h-2.546V8.663c0-1.56-.617-2.35-1.848-2.35-1.112 0-1.668.668-1.67 1.977v6.218H4.822V8.102c0-1.31.337-2.35 1.011-3.12.696-.77 1.608-1.164 2.74-1.164 1.311 0 2.302.5 2.962 1.498l.638 1.06.638-1.06c.66-.999 1.65-1.498 2.96-1.498 1.13 0 2.043.395 2.74 1.164.675.77 1.012 1.81 1.012 3.12z"/></svg> },
                { name: 'Google Business', color: '#4285F4', icon: <svg viewBox="0 0 24 24" className="w-8 h-8" fill="currentColor"><path d="M12 0C5.372 0 0 5.373 0 12s5.372 12 12 12c6.627 0 12-5.373 12-12S18.627 0 12 0zm.14 19.018c-3.868 0-7-3.14-7-7.018 0-3.878 3.132-7.018 7-7.018 1.89 0 3.47.697 4.682 1.829l-1.974 1.978v-.004c-.735-.702-1.667-1.062-2.708-1.062-2.31 0-4.187 1.956-4.187 4.273 0 2.315 1.877 4.277 4.187 4.277 2.096 0 3.522-1.202 3.816-2.852H12.14v-2.737h6.585c.088.47.135.96.135 1.474 0 4.01-2.677 6.86-6.72 6.86z"/></svg> },
                { name: 'Tumblr', color: '#36465D', icon: <svg viewBox="0 0 24 24" className="w-8 h-8" fill="currentColor"><path d="M14.563 24c-5.093 0-7.031-3.756-7.031-6.411V9.747H5.116V6.648c3.63-1.313 4.512-4.596 4.71-6.469C9.84.051 9.941 0 9.999 0h3.517v6.114h4.801v3.633h-4.82v7.47c.016 1.001.375 2.371 2.207 2.371h.09c.631-.02 1.486-.205 1.936-.419l1.156 3.425c-.436.636-2.4 1.374-4.156 1.404h-.178l-.009.002z"/></svg> },
                { name: 'Twitch', color: '#9146FF', icon: <svg viewBox="0 0 24 24" className="w-8 h-8" fill="currentColor"><path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z"/></svg> },
              ];
              const itemsPerPage = 6;
              const totalPages = Math.ceil(platforms.length / itemsPerPage);
              const visiblePlatforms = platforms.slice(integrationIndex * itemsPerPage, (integrationIndex + 1) * itemsPerPage);
              
              return (
                <>
                  <div className="flex items-center justify-between gap-4 mt-8">
                    <button 
                      onClick={() => setIntegrationIndex(i => i > 0 ? i - 1 : totalPages - 1)}
                      aria-label="Previous integrations"
                      className="w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center text-gray-400 hover:text-purple-600 hover:shadow-xl transition-all flex-shrink-0"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>

                    <div className="flex-1 flex justify-center items-center gap-4 md:gap-8 py-4 overflow-hidden">
                      {visiblePlatforms.map((platform, idx) => (
                        <div 
                          key={platform.name}
                          className={`flex flex-col items-center gap-2 transition-all duration-300 ${idx === Math.floor(visiblePlatforms.length / 2) ? 'transform scale-110' : 'hover:scale-105'}`}
                        >
                          <div 
                            className={`w-14 h-14 md:w-16 md:h-16 rounded-xl flex items-center justify-center transition-all ${idx === Math.floor(visiblePlatforms.length / 2) ? 'bg-white shadow-xl border-2 border-purple-300' : 'bg-white/80 shadow-md hover:shadow-lg'}`}
                            style={{ color: platform.color }}
                          >
                            {platform.icon}
                          </div>
                          <span className="text-xs font-medium text-gray-600 whitespace-nowrap">{platform.name}</span>
                        </div>
                      ))}
                    </div>

                    <button 
                      onClick={() => setIntegrationIndex(i => i < totalPages - 1 ? i + 1 : 0)}
                      aria-label="Next integrations"
                      className="w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center text-gray-400 hover:text-purple-600 hover:shadow-xl transition-all flex-shrink-0"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Page Indicators */}
                  <div className="flex justify-center gap-2 mt-6">
                    {Array.from({ length: totalPages }).map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setIntegrationIndex(i)}
                        aria-label={`Go to page ${i + 1}`}
                        className={`transition-all ${i === integrationIndex ? 'w-8 h-3 bg-purple-600 rounded-full' : 'w-3 h-3 bg-gray-300 rounded-full hover:bg-purple-300'}`}
                      />
                    ))}
                  </div>
                </>
              );
            })()}

            <div className="flex justify-center gap-2 mt-8">
              <div className="w-3 h-3 bg-purple-600 rounded-full"></div>
              <div className="w-8 h-3 bg-gray-300 rounded-full"></div>
              <div className="w-3 h-3 bg-gray-300 rounded-full"></div>
            </div>
          </div>

          {/* Final CTA Button */}
          <div className="text-center mt-20">
            <Link href="/signup" className="relative group inline-block">
              <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 via-pink-500 to-orange-500 rounded-full blur-lg opacity-60 group-hover:opacity-100 transition duration-300"></div>
              <div className="relative px-12 py-5 bg-black rounded-full leading-none flex items-center justify-center">
                <span className="text-white font-bold text-xl mr-2">Switch to SHOWBOLD</span>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-400 to-orange-400 font-bold text-xl">AI</span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 relative overflow-hidden bg-[#050511] reveal-on-scroll">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-900/20 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-pink-900/15 rounded-full filter blur-3xl"></div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 glow-text">Frequently Asked Questions</h2>
            <p className="text-gray-400 text-lg">Everything you need to know about SHOWBOLD</p>
          </div>

          <div className="space-y-4">
            {[
              { q: "What is SHOWBOLD?", a: "SHOWBOLD is an AI-powered marketing platform that acts as your complete marketing department. We handle content creation, scheduling, analytics, and optimization across all major social platforms - all powered by cutting-edge AI." },
              { q: "How does the free trial work?", a: "Start with our Free plan forever, or try any paid plan free for 14 days. No credit card required. Cancel anytime with no questions asked." },
              { q: "Which social platforms do you support?", a: "We support 18+ platforms including Instagram, TikTok, X (Twitter), LinkedIn, Facebook, YouTube, Pinterest, Threads, and many more. Connect all your accounts from one dashboard." },
              { q: "Can I cancel my subscription anytime?", a: "Yes! You can cancel your subscription at any time. If you cancel, you'll retain access until the end of your billing period. No hidden fees or cancellation penalties." },
              { q: "Is my data secure?", a: "Absolutely. We use enterprise-grade encryption, never sell your data, and comply with GDPR and SOC 2 standards. Your content and analytics are yours alone." },
              { q: "Do you offer team collaboration?", a: "Yes! Pro plans and above include team members with role-based permissions, approval workflows, and collaborative workspaces. Agency plans support up to 50 client workspaces." },
              { q: "What makes SHOWBOLD different from other tools?", a: "Unlike schedulers or basic AI writers, SHOWBOLD is a complete AI marketing department. We generate content, images, videos, and voiceovers. We optimize timing, track competitors, and provide AI coaching - all in one platform." },
              { q: "Can I white-label SHOWBOLD for my agency?", a: "Yes! Agency and Enterprise plans include white-label options so you can offer SHOWBOLD's power under your own brand to your clients." },
            ].map((faq, i) => (
              <details key={i} className="group bg-[#0f172a]/60 backdrop-blur-xl border border-gray-800 rounded-2xl overflow-hidden">
                <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                  <span className="text-white font-semibold text-lg pr-4">{faq.q}</span>
                  <ChevronDown className="w-5 h-5 text-gray-400 group-open:rotate-180 transition-transform flex-shrink-0" />
                </summary>
                <div className="px-6 pb-6 text-gray-400 leading-relaxed">
                  {faq.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 relative overflow-hidden reveal-on-scroll">
        <div className="absolute inset-0 bg-white/95 backdrop-blur-sm"></div>
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-200/30 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-pink-200/30 rounded-full filter blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Loved by Marketers Worldwide</h2>
            <p className="text-gray-600 text-lg">See what our customers are saying</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { name: "Sarah Chen", role: "Marketing Director", company: "TechFlow", quote: "SHOWBOLD replaced our entire content team. We went from 10 hours/week on social to 30 minutes. The AI-generated content actually performs better than what we wrote manually.", avatar: "👩‍💼" },
              { name: "Marcus Johnson", role: "Founder", company: "Bloom Studio", quote: "I was skeptical about AI marketing, but SHOWBOLD changed everything. Our engagement is up 340% and I finally have time to focus on my actual business.", avatar: "👨‍💻" },
              { name: "Elena Rodriguez", role: "Social Media Manager", company: "Velocity", quote: "The autopilot feature is insane. I schedule a month of content in one session, and the AI optimizes everything. My boss thinks I'm a genius.", avatar: "👩‍🎨" },
              { name: "David Park", role: "Agency Owner", company: "Pinnacle Media", quote: "We manage 40+ clients now with a team of 3. Before SHOWBOLD, that would've required 15 people. The white-label feature lets us charge premium rates.", avatar: "👨‍💼" },
              { name: "Jessica Williams", role: "E-commerce Owner", company: "Luxe Boutique", quote: "The mockup generator alone is worth the subscription. Professional product shots in seconds that used to cost me $500 per shoot.", avatar: "👩‍🦰" },
              { name: "Ryan O'Connor", role: "Content Creator", company: "1.2M Followers", quote: "I post across 6 platforms daily without burning out. SHOWBOLD handles the repurposing and timing. My audience growth has never been faster.", avatar: "🧔" },
            ].map((testimonial, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 shadow-xl border border-gray-100 hover:shadow-2xl transition-shadow">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center text-2xl">
                    {testimonial.avatar}
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-500">{testimonial.role} at {testimonial.company}</div>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed">&ldquo;{testimonial.quote}&rdquo;</p>
                <div className="flex gap-1 mt-4">
                  {[...Array(5)].map((_, j) => (
                    <svg key={j} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative py-16 border-t border-gray-800 overflow-hidden">
        {/* Footer Aurora - Static for performance */}
        <div className="absolute inset-0 bg-[#050510]"></div>
        <div className="absolute inset-0">
          <div className="absolute bottom-0 left-1/4 w-80 h-80 bg-purple-900/20 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-pink-900/15 rounded-full filter blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex flex-col md:flex-row justify-between items-center mb-8">
            <div className="flex space-x-6 text-sm mb-4 md:mb-0 text-gray-400">
              <a href="#features" className="hover:text-white transition-colors">Features</a>
              <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
              <Link href="/contact" className="hover:text-white transition-colors">Contact</Link>
            </div>
            <div className="flex space-x-6 text-sm text-gray-400">
              <Link href="/terms" className="hover:text-white transition-colors">Terms</Link>
              <Link href="/privacy" className="hover:text-white transition-colors">Privacy</Link>
              <Link href="/login" className="hover:text-white transition-colors">Log In</Link>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center text-xs text-gray-500">
            <p>© 2026 SHOWBOLD. All rights reserved.</p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <a href="#" className="hover:text-purple-500 transition-colors" aria-label="X (Twitter)">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              </a>
              <a href="#" className="hover:text-purple-500 transition-colors" aria-label="Facebook">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>
              <a href="#" className="hover:text-purple-500 transition-colors" aria-label="Instagram">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
              </a>
              <a href="#" className="hover:text-purple-500 transition-colors" aria-label="YouTube">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
              </a>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}
